import { Box, IconButton, Theme, Typography } from "@outschool/backpack";
import { Activity } from "@outschool/gql-backend-generated";
import { NextMeetingsOfParentReminderQueryQuery } from "@outschool/gql-frontend-generated";
import { faTimes } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { useSession } from "@outschool/ui-auth";
import { AvatarImage, DEFAULT_AVATAR } from "@outschool/ui-components-shared";
import React, { useState } from "react";

import { useNextMeetingsOfParentReminder } from "../../../hooks/JoinMeeting";
import { useTimeZone } from "../../../providers/TimeZoneProvider";
import { LiveMeetingButtonWithVideo } from "../../../routes/Classroom";
import { useLiveFromNow } from "../../loggedInHome/hooks/useLiveFromNow";

const JOIN_MEETING_TRACKING_EVENT = "join_meeting_from_parent_dashboard";
const START_MEETING_TRACKING_EVENT = "start_meeting_from_parent_dashboard";

function NextMeetingsReminder({
  data,
}: {
  data: NextMeetingsOfParentReminderQueryQuery["nextMeetingsOfParentReminder"][number];
}) {
  const { t } = useTranslation(
    "client\\components\\parentDashboardV2\\components\\NextMeetingBanner",
  );
  const { timeZone: userTimeZone } = useTimeZone();
  const { currentUser } = useSession();
  const learner = currentUser?.children.find(
    child => child.uid === data.learnerUid,
  );

  const fromNow = useLiveFromNow(data.startTime);

  if (!learner) {
    return null;
  }

  return (
    <Box
      flex
      justifyContent="space-between"
      alignItems="center"
      flexDirection="column"
      sx={(theme: Theme) => ({
        borderRadius: 0,
        "&:only-child": {
          borderRadius: 12,
        },
        "&:first-child:not(:only-child)": {
          borderRadius: "12px 12px 0 0",
        },
        "&:last-child:not(:only-child)": {
          borderRadius: "0 0 12px 12px",
        },
        gap: 8,
        [theme.breakpoints.up("lg")]: {
          flexDirection: "row",
          gap: 32,
        },
      })}
    >
      <Box
        flex
        alignItems="center"
        flexDirection="column"
        sx={(theme: Theme) => ({
          gap: 8,
          [theme.breakpoints.up("lg")]: {
            flexDirection: "row",
            gap: 0,
          },
          minWidth: 0,
        })}
      >
        <Box flex alignItems="center" sx={{ marginRight: 32, flexShrink: 0 }}>
          <AvatarImage
            avatar={learner.avatar || DEFAULT_AVATAR}
            name={learner.name}
            width={35}
            height={35}
          />
          <Typography variant="h6" sx={{ marginLeft: 12 }}>
            {learner.name}
          </Typography>
        </Box>
        <Box
          sx={{
            minWidth: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={(theme: Theme) => ({
              textAlign: "center",
              [theme.breakpoints.up("lg")]: {
                textOverflow: "ellipsis",
                overflow: "hidden",
                whiteSpace: "nowrap",
                textAlign: "left",
              },
            })}
          >
            {data.activity.title}
          </Typography>
        </Box>
      </Box>
      <Box
        flex
        alignItems="center"
        flexDirection="column"
        sx={(theme: Theme) => ({
          gap: 8,
          [theme.breakpoints.up("lg")]: {
            flexDirection: "row",
            gap: 20,
          },
          flexShrink: 0,
        })}
      >
        <Typography variant="subtitle2">
          {dayjs().isBefore(data.startTime) ? (
            <span>{t("Starting {{fromNow}}", { fromNow })}</span>
          ) : (
            <span>{t("Started {{fromNow}}", { fromNow })}</span>
          )}
        </Typography>
        {data.learnerUid && learner.name && (
          <LiveMeetingButtonWithVideo
            activity={data.activity as Activity}
            sectionUid={data.sectionUid}
            learnerUid={data.learnerUid}
            learnerName={learner.name}
            userTimeZone={userTimeZone}
            canManage={data.canManage}
            shouldShowOnlyButton
            joinButtonLabel={t("Join now")}
            joinTrackingLabel={
              data.canManage
                ? START_MEETING_TRACKING_EVENT
                : JOIN_MEETING_TRACKING_EVENT
            }
            size="small"
            variant="outlined"
            sx={{ border: "none !important" }}
          />
        )}
      </Box>
    </Box>
  );
}

export default function NextMeetingBanner() {
  const { isLoggedIn } = useSession();
  const { data, loading } = useNextMeetingsOfParentReminder(!isLoggedIn);
  const [visible, setVisible] = useState(true);

  const nextMeetings = data?.nextMeetingsOfParentReminder;

  const nextMeetingsInDisplayRange = nextMeetings?.filter(data => {
    const startTime = dayjs(data.startTime);
    const fifteenMinutesAfterStart = startTime.add(15, "minutes");
    const fortyFiveMinutesBeforeStart = startTime.subtract(45, "minutes");

    return dayjs().isBetween(
      fortyFiveMinutesBeforeStart,
      fifteenMinutesAfterStart,
    );
  });

  if (
    !visible ||
    loading ||
    !nextMeetingsInDisplayRange ||
    nextMeetingsInDisplayRange.length === 0
  ) {
    return null;
  }

  return (
    <Box
      sx={(theme: Theme) => ({
        position: "fixed",
        bottom: 72,
        display: "flex",
        justifyContent: "center",
        width: "100%",
        pointerEvents: "none",
        zIndex: 200,
        [theme.breakpoints.down("md")]: {
          bottom: 64,
          maxHeight: 192,
        },
      })}
    >
      <Box
        flex
        flexDirection="column"
        gap={20}
        sx={(theme: Theme) => ({
          pointerEvents: "auto",
          backgroundColor: theme.palette.primary[700],
          borderRadius: 40,
          color: theme.palette.common.white,
          px: 40,
          py: 20,
          maxWidth: "60vw",
          [theme.breakpoints.down("md")]: {
            px: 20,
            maxWidth: "100%",
            width: "100%",
            borderRadius: 0,
            overflowY: "auto",
            overflowX: "hidden",
          },
        })}
      >
        <IconButton
          variant="text"
          sx={(theme: Theme) => ({
            display: "none",
            [theme.breakpoints.down("md")]: {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "absolute",
              top: 8,
              right: 8,
              height: 32,
              width: 32,
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1) !important",
              },
            },
          })}
          icon={faTimes}
          onClick={() => setVisible(false)}
        />
        {nextMeetingsInDisplayRange.map(data => (
          <NextMeetingsReminder data={data} key={data.learnerUid} />
        ))}
      </Box>
    </Box>
  );
}
